import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { PortalLayout } from '../components/PortalLayout';
import { PortalSidebar } from '../components/PortalSidebar';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { ScrollReveal } from '../components/ScrollReveal';
import { ruleGroups, schedules, rulesMeta, rulesSidebarGroups, enforcementTimeline } from '../data/rulesContent';
import { FileDown, ArrowRight } from 'lucide-react';

const DpdpRules = () => (
  <>
    <SEO
      title="DPDP Rules 2025 Explained — All 23 Rules & 7 Schedules"
      description="Full breakdown of the DPDP Rules, 2025 — notice format, Consent Manager registration, breach reporting, security safeguards, children's data and the compliance timeline to 13 May 2027."
      path="/dpdp-rules"
    />
    <PortalLayout
      breadcrumb={<Breadcrumbs trail={[{ label: 'DPDP Rules, 2025' }]} />}
      sidebar={<PortalSidebar heading="DPDP Rules, 2025" headingHref="/dpdp-rules" groups={rulesSidebarGroups} />}
    >
      <div data-testid="dpdp-rules-page" className="max-w-3xl">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-saffron-600 mb-3">Notified by {rulesMeta.authority} · {rulesMeta.notified}</p>
        <h1 className="font-serif text-3xl sm:text-4xl text-navy leading-tight mb-4">DPDP Rules, 2025 — the "how" of the DPDPA</h1>
        <p className="text-gray-600 leading-relaxed mb-6">
          The Act sets the principles; the Rules set the procedure. {rulesMeta.rules} rules and {rulesMeta.schedules} schedules prescribe notice formats, Consent Manager registration, security safeguards, the 72-hour breach workflow, erasure timelines, verifiable parental consent and how the Board functions.
        </p>
        <a
          href={rulesMeta.officialPdf}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="rules-download-pdf-link"
          className="inline-flex items-center gap-2 text-sm text-navy hover:text-saffron-600 font-medium border border-gray-200 rounded-sm px-4 py-2 mb-12"
        >
          <FileDown size={14} /> Download official Rules text (PDF)
        </a>

        <div className="space-y-14" data-testid="rules-groups-list">
          {ruleGroups.map((group) => (
            <ScrollReveal key={group.id}>
              <h2 className="font-serif text-2xl text-navy mb-5">{group.title}</h2>
              <div className="border-t border-gray-200">
                {group.rules.map((r) => (
                  <Link
                    key={r.code}
                    to={`/dpdp-rules/rule/${r.code}`}
                    data-testid={`rule-link-${r.code}`}
                    className="group flex items-center justify-between gap-4 py-3 border-b border-gray-200 hover:pl-2 transition-all duration-200"
                  >
                    <span className="flex gap-3 items-baseline">
                      <span className="font-mono text-xs text-gray-400 shrink-0 w-10">{r.code}</span>
                      <span className="text-navy font-medium group-hover:text-saffron-600 transition-colors">{r.title}</span>
                    </span>
                    <ArrowRight size={14} className="text-gray-300 group-hover:text-saffron shrink-0 group-hover:translate-x-1 transition-all" />
                  </Link>
                ))}
              </div>
            </ScrollReveal>
          ))}
        </div>

        <div className="bg-navy rounded-sm mt-16 p-8" data-testid="rules-schedules-section">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-saffron mb-3">The seven Schedules to the Rules</p>
          <h2 className="font-serif text-2xl text-white mb-8">Schedules, decoded</h2>
          <div className="space-y-5">
            {schedules.map((s) => (
              <div key={s.id} data-testid={`rules-schedule-card-${s.id}`} className="border border-white/10 p-5 hover:border-saffron/50 transition-colors">
                <p className="font-mono text-xs text-white/40 mb-1">{s.ref}</p>
                <p className="font-serif text-lg text-saffron mb-2">{s.title}</p>
                <p className="text-sm text-white/60 leading-relaxed mb-3">{s.summary}</p>
                {s.parts.map((p) => (
                  <div key={p.label} className="mt-3">
                    <p className="text-xs uppercase tracking-widest text-white/40 mb-2">{p.label}</p>
                    <ul className="space-y-1.5">
                      {p.items.map((item, i) => (
                        <li key={i} className="text-sm text-white/75 leading-relaxed flex gap-2">
                          <span className="text-saffron shrink-0">·</span>{item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16" data-testid="rules-timeline-section">
          <h2 className="font-serif text-2xl text-navy mb-6">Enforcement timeline</h2>
          <div>
            {enforcementTimeline.map((m) => (
              <div key={m.date} className="flex flex-col sm:flex-row gap-3 sm:gap-8 py-5 border-b border-gray-200">
                <p className="font-mono text-sm text-saffron-600 w-36 shrink-0">{m.date}</p>
                <div>
                  <p className="font-serif text-lg text-navy mb-1">{m.title}</p>
                  <p className="text-sm text-gray-500 leading-relaxed">{m.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </PortalLayout>
  </>
);

export default DpdpRules;
